import React, { useContext, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import PropTypes from 'prop-types'
import swal from 'sweetalert2'
import prestigeServices from '@enums/prestigeServices'

import {
  Box,
  Button,
  DashboardModule,
  FormField,
  ListItem,
  LoadingModule,
} from '@campaignhub/suit-theme'

import { useSetState } from '@campaignhub/react-hooks'
import PageContext from '@contexts/pageContext'
import useServiceJob from '@hooks/useServiceJob'
import useWorkflow from '@hooks/useWorkflow'
import { getEntityByName } from '@functions/getEntityByName'

import Icons from '@components/Icons'

import ServiceJobCritical from './components/ServiceJobCritical'
import ServiceJobTags from './components/ServiceJobTags'
import ServiceJobDeadlines from './components/ServiceJobDeadlines'

const loadWorkflow = (workflow, loadFn, setState) => {
  loadFn(workflow).then(({ success, data }) => {
    if (!success && data) {
      toast.warning(data[0])
      return
    }

    setState({ workflow: data })
  })
}

const defaultState = {
  isWorkflowCommentValid: true,
  statusId: 0,
  workflow: {},
  workflowComment: '',
}

const JobDetails = (props) => {
  const { showStudioDeadline, hidden } = props

  const {
    callbacks: { assignServiceJobStatus },
    serviceJob,
    workflowUpdated,
  } = useContext(PageContext)
  const { status } = serviceJob

  const entities = useSelector(reduxState => reduxState.entities)
  const { serviceJobHistories, serviceJobStatuses } = entities

  const { loading: serviceJobHistoriesLoading } = useSelector(reduxState => reduxState.serviceJobHistories)

  const [state, setState] = useSetState(defaultState)
  const {
    isWorkflowCommentValid, statusId, workflow, workflowComment,
  } = state

  const serviceJobPayload = useServiceJob({ id: serviceJob.id })
  const {
    callbacks: { assignServiceJobStatus: createFn },
    loading: serviceJobLoading,
  } = serviceJobPayload

  useEffect(() => {
    setState({ statusId: serviceJob.serviceJobStatusId })
  }, [serviceJob, workflowUpdated])

  const workflowPayload = useWorkflow()
  const {
    callbacks: { loadWorkflow: loadFn },
    loaded,
    loading: workflowLoading,
  } = workflowPayload

  useEffect(() => {
    loadWorkflow({ id: 1 }, loadFn, setState)
  }, [loaded])

  const saveServiceJobState = () => {
    setState({ isWorkflowCommentValid: workflowComment.length > 0 })

    if (workflowComment.length > 0) {
      const serviceJobParam = {
        id: serviceJob.id,
        serviceJobStatusId: statusId,
        workflowComment,
      }
      assignServiceJobStatus(serviceJobParam, createFn)
    }
  }

  const archiveServiceJob = (archive) => {
    const histories = Object.values(serviceJobHistories)
      ?.filter(x => x?.serviceJobId === serviceJob.id)
      ?.sort((x, y) => y.id - x.id) || []

    const previousStatus = histories.find(x => (
      x.serviceJobStatusId !== serviceJob.serviceJobStatusId && x.serviceJobStatusId !== getEntityByName(serviceJobStatuses, 'Archived')?.id
    ))

    const statusBeforeArchived = previousStatus ? previousStatus.serviceJobStatusId : getEntityByName(serviceJobStatuses, 'Ordered')?.id

    const serviceJobParam = {
      id: serviceJob.id,
      serviceJobStatusId: archive ? getEntityByName(serviceJobStatuses, 'Archived')?.id : statusBeforeArchived,
      workflowComment: `${archive ? 'Archived' : 'Unarchived'} on service job page`,
    }

    swal.fire({
      title: `${archive ? 'Archive' : 'Unarchive'}`,
      html: `This action will ${archive ? 'archive' : 'unarchive'} this job.`
        + '<br/>Do you wish to proceed?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes',
      confirmButtonColor: '#e2001a',
      showClass: 'slide-from-top',
    }).then(({ value }) => {
      if (value) {
        assignServiceJobStatus(serviceJobParam, createFn)
      }
    })
  }

  useEffect(() => {
    if (workflow.serviceJobStatuses
      && workflow.serviceJobStatuses.find(e => e.id === 60)
      && prestigeServices.find(x => x.id === serviceJob.service?.id)
      && serviceJob.serviceJobStatusId !== 60) {
      workflow.serviceJobStatuses = workflow.serviceJobStatuses.filter(item => item.id !== 60)
      setState({ workflow })
    } else if (prestigeServices.find(x => x.id === serviceJob.service?.id) && statusId === 60 && Object.keys(serviceJobStatuses).length > 0) {
      workflow.serviceJobStatuses = Object.values(serviceJobStatuses)
      setState({ workflow })
    }
  }, [statusId, serviceJobStatuses])

  if (hidden) return null

  return (
    <>
      <LoadingModule loading={serviceJobLoading || workflowLoading} />
      {!serviceJobLoading && !workflowLoading && (
        <DashboardModule title="Job Details">
          <Box flexDirection="column">
            <ListItem
              boxProps={{
                borderBottom: '1px solid',
                padding: 'large',
              }}
              flexDirection="column"
              style={{ cursor: 'pointer' }}
              disableHover
            >
              <ServiceJobDeadlines showStudioDeadline={showStudioDeadline} />
              <ServiceJobTags />
            </ListItem>
            <ListItem
              boxProps={{
                borderBottom: '1px solid',
                padding: 'large',
              }}
              flexDirection="column"
              disableHover
            >
              <ServiceJobCritical serviceJobId={serviceJob.id} />
            </ListItem>
            <ListItem
              boxProps={{
                borderBottom: '1px solid',
                padding: 'large',
              }}
              flexDirection="column"
              disableHover
            >
              <Box flexDirection="column">
                <FormField label="Status">
                  <select onChange={e => setState({ statusId: e.target.value })} value={statusId || status?.id}>
                    {workflow.serviceJobStatuses?.map(serviceJobStatus => (
                      <option key={serviceJobStatus.id} value={serviceJobStatus.id}>
                        {serviceJobStatus?.description}
                      </option>
                    ))}
                  </select>
                </FormField>
              </Box>
              <Box flexDirection="column" paddingTop="small" paddingBottom="small">
                <FormField errorMessage={!isWorkflowCommentValid ? 'Required' : ''}>
                  <textarea
                    type="text"
                    onChange={ev => (setState({ workflowComment: ev.target.value.trim() }))}
                    placeholder="Reason required for change"
                    style={{ height: 80, resize: 'vertical' }}
                  />
                </FormField>
              </Box>
              <Button buttonStyle="secondaryUtility" size="medium" onClick={saveServiceJobState}>
                Change Status
              </Button>
            </ListItem>
            <ListItem
              boxProps={{
                borderBottom: '1px solid',
                padding: 'large',
              }}
              flexDirection="column"
              disableHover
            >
              <Box flexDirection="row">
                <Box flexDirection="column" marginRight="small">
                  <Button
                    buttonStyle="secondaryUtility"
                    disabled={serviceJob.serviceJobStatusId === getEntityByName(serviceJobStatuses, 'Archived')?.id}
                    onClick={() => archiveServiceJob(1)}
                    size="medium"
                  >
                    Archive
                  </Button>
                </Box>
                <Box flexDirection="column" width="auto" flexShrink="0">
                  <Button
                    buttonStyle="secondaryUtility"
                    disabled={serviceJob.serviceJobStatusId !== getEntityByName(serviceJobStatuses, 'Archived')?.id}
                    height={37.5}
                    loading={serviceJobHistoriesLoading}
                    onClick={() => archiveServiceJob(0)}
                    size="medium"
                  >
                    <Icons name="Unarchive" />
                  </Button>
                </Box>
              </Box>
            </ListItem>
          </Box>
        </DashboardModule>
      )}
    </>
  )
}

JobDetails.propTypes = {
  showStudioDeadline: PropTypes.bool,
  hidden: PropTypes.bool,
}

export default JobDetails
